import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SharedService } from '@services/shared.service';
import { PagesService } from '@services/pages.service';
import { SharedModule } from '@shared/shared.module';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSelectModule } from 'ng-zorro-antd/select';

interface Application {
  id: number;
  name: string;
  platform: string;
  status: string;
  created_at?: string;
  application_id?: string;
  api_key?: string;
}

@Component({
  selector: 'app-sdk-applications',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzDrawerModule,
    NzToolTipModule,
    NzPopconfirmModule,
    NzSelectModule,
    SharedModule
  ],
  templateUrl: './sdk-applications.component.html',
  styles: [`
    .sdk-applications-page {
      width: 100%;
    }
    .sdk-header {
      margin-bottom: 40px;
    }
    .sdk-icon {
      width: 80px;
      height: 80px;
      background: linear-gradient(135deg, #f5a623 0%, #f57c00 100%);
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 20px;
    }
    .sdk-icon i {
      font-size: 36px;
      color: #fff;
    }
    .sdk-header h2 {
      font-size: 24px;
      font-weight: 700;
      color: #1E005E;
      margin-bottom: 8px;
    }
    .sdk-header p {
      color: #666;
      font-size: 14px;
      margin: 0;
    }
    .sdk-section {
      background: #fff;
      border: 1px solid #e0e0e0;
      border-radius: 12px;
      padding: 24px;
      margin-bottom: 20px;
    }
    .sdk-section h4 {
      font-size: 14px;
      font-weight: 600;
      color: #1E005E;
      margin-bottom: 12px;
    }
    .info-box {
      background: #f5f5f9;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      padding: 14px 16px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .info-box span {
      font-size: 14px;
      color: #333;
    }
    .app-badges {
      display: flex;
      gap: 8px;
    }
    .platform-badge, .status-badge {
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 11px;
      font-weight: 500;
    }
    .status-badge {
      color: #fff;
    }
    .api-key-box {
      display: flex;
      align-items: center;
      background: #f5f5f9;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      overflow: hidden;
    }
    .api-key-box .form-control {
      border: none;
      background: transparent;
      padding: 14px 16px;
      font-size: 13px;
      color: #666;
    }
    .api-key-box .form-control:focus {
      box-shadow: none;
    }
    .api-key-box .copy-btn {
      background: #fff;
      border: none;
      border-left: 1px solid #e0e0e0;
      padding: 14px 20px;
      font-size: 13px;
      font-weight: 500;
      color: #1E005E;
      cursor: pointer;
      transition: all 0.3s;
    }
    .api-key-box .copy-btn:hover {
      background: #f0f0f5;
    }
    .add-new-section {
      border-style: dashed;
      background: #fafafa;
    }
    .add-new-section h4 {
      margin-bottom: 8px;
    }
    .add-new-section p {
      font-size: 13px;
      margin-bottom: 16px;
    }

    /* Phone: shrink the type so the app name + badges fit the narrow row
       without the name wrapping awkwardly. */
    @media (max-width: 768px) {
      .sdk-section {
        padding: 16px;
      }
      .sdk-section h4 {
        font-size: 13px;
      }
      .info-box {
        padding: 10px 12px;
        gap: 8px;
      }
      .info-box span {
        font-size: 12px;
      }
      .app-badges {
        gap: 6px;
        flex-shrink: 0;
      }
      .platform-badge, .status-badge {
        padding: 3px 9px;
        font-size: 10px;
      }
      .api-key-box .form-control,
      .api-key-box .copy-btn {
        padding: 11px 12px;
        font-size: 12px;
      }
      .sdk-section .btn-primary {
        padding: 9px 14px;
        font-size: 13px;
        height: auto;
        min-height: 0;
      }
    }
  `]
})
export class SdkApplicationsComponent implements OnInit {
  private fb = inject(FormBuilder);
  private sharedService = inject(SharedService);
  private pagesService = inject(PagesService);

  applications: Application[] = [];
  applicationForm!: FormGroup;
  drawerVisible = false;
  isLoading = false;
  submitted = false;

  platforms = [
    { value: 'ios', label: 'iOS' },
    { value: 'android', label: 'Android' },
    { value: 'web', label: 'Web' },
    { value: 'all', label: 'All Platforms' }
  ];

  ngOnInit(): void {
    this.initForm();
    this.loadApplications();
  }

  initForm(): void {
    this.applicationForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      platform: ['', [Validators.required]],
      status: ['active', [Validators.required]]
    });
  }

  get filteredApplications(): Application[] {
    return this.applications;
  }

  loadApplications(): void {
    this.isLoading = true;
    this.sharedService.showLoader();
    this.pagesService.getAll('SDK_APPLICATIONS_LIST').subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : [];
        // Map api_key from api_keys array
        this.applications = data.map((app: any) => ({
          ...app,
          api_key: app.api_keys?.length > 0 ? app.api_keys[0].api_key : null
        }));
        this.isLoading = false;
        this.sharedService.hideLoader();
      },
      error: () => {
        this.applications = [];
        this.isLoading = false;
        this.sharedService.hideLoader();
      }
    });
  }

  generateApiKey(appId: number): void {
    this.sharedService.showLoader();
    this.pagesService.update('SDK_GENERATE_API_KEY', { application_id: appId }).subscribe({
      next: () => {
        this.sharedService.createNotification('success', 'API Key generated successfully');
        this.sharedService.hideLoader();
        this.loadApplications();
      },
      error: (err: any) => {
        this.sharedService.createNotification('error', err?.error?.message || 'Failed to generate API Key');
        this.sharedService.hideLoader();
      }
    });
  }

  openAddDrawer(): void {
    this.submitted = false;
    this.applicationForm.reset({ status: 'active' });
    this.drawerVisible = true;
  }

  closeDrawer(): void {
    this.drawerVisible = false;
    this.applicationForm.reset({ status: 'active' });
    this.submitted = false;
  }

  isFieldInvalid(field: string): boolean {
    const control = this.applicationForm.get(field);
    return !!(control && control.invalid && (control.dirty || control.touched || this.submitted));
  }

  submitForm(): void {
    this.submitted = true;
    if (this.applicationForm.invalid) {
      Object.keys(this.applicationForm.controls).forEach(key => {
        this.applicationForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.sharedService.showLoader();
    const formData = this.applicationForm.value;

    this.pagesService.update('SDK_APPLICATIONS', formData).subscribe({
      next: () => {
        this.sharedService.createNotification('success', 'Application created successfully');
        this.sharedService.hideLoader();
        this.closeDrawer();
        this.loadApplications();
      },
      error: (err: any) => {
        this.sharedService.createNotification('error', err?.error?.message || 'Failed to create application');
        this.sharedService.hideLoader();
      }
    });
  }

  copyApiKey(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.sharedService.createNotification('success', 'Copied to clipboard');
    });
  }

  getPlatformStyle(platform: string): { [key: string]: string } {
    const styles: { [key: string]: { background: string; color: string } } = {
      ios: { background: '#e3f2fd', color: '#1976d2' },
      android: { background: '#e8f5e9', color: '#388e3c' },
      web: { background: '#fff3e0', color: '#f57c00' },
      all: { background: '#f3e5f5', color: '#7b1fa2' }
    };
    return {
      'background-color': styles[platform]?.background || '#f5f5f5',
      'color': styles[platform]?.color || '#666'
    };
  }

  getPlatformLabel(platform: string): string {
    const labels: { [key: string]: string } = {
      ios: 'iOS',
      android: 'Android',
      web: 'Web',
      all: 'All'
    };
    return labels[platform] || platform;
  }
}
